import { Avatar, Box, Text } from '@factorialco/deprecated-design-system'
import { Delete } from '@factorialco/f0-react/icons/app'

import buildUrl from 'modules/core/lib/buildUrl'

import { type StrategyItem, getStrategyOptions } from './manualApproveHandlerUtils'

interface ApproverSelectionProps {
  strategy: StrategyItem
  onDelete: (strategyId: string) => void
  availableEmployees: Array<{
    id: number
    fullName: string
    avatar?: { url?: string | null } | null
  }>
  availableTeams: Array<{ id: number; name: string }>
  availableRoles?: Array<{ id: number; name: string }>
  treeRoleNameMap?: Map<string, string>
}

export const ApproverSelection = ({
  strategy,
  onDelete,
  availableEmployees,
  availableTeams,
  availableRoles = [],
  treeRoleNameMap = new Map(),
}: ApproverSelectionProps) => {
  const displayInfo = getStrategyDisplayInfo(
    strategy,
    availableEmployees,
    availableTeams,
    availableRoles,
    treeRoleNameMap
  )

  return (
    <Box
      flexDirection="row"
      gap="s8"
      alignItems="center"
      justifyContent="spaceBetween"
      padding="s8"
      borderRadius={{ all: 'abs008' }}
      className="hover:bg-f1-background-hover group"
    >
      <Box flexDirection="row" gap="s8" alignItems="center" width="full">
        <Avatar
          size="s24"
          label={displayInfo.avatarText}
          {...(displayInfo.avatarSrc ? { url: displayInfo.avatarSrc } : {})}
        />
        <Text size="200" weight="medium">
          {displayInfo.label}
        </Text>
      </Box>
      <Delete
        width={20}
        height={20}
        onClick={() => onDelete(strategy.id)}
        className="opacity-0 group-hover:opacity-100 cursor-pointer text-f1-foreground-critical hover:text-f1-foreground-critical-hover"
      />
    </Box>
  )
}

const getStrategyDisplayInfo = (
  strategy: StrategyItem,
  availableEmployees: Array<{
    id: number
    fullName: string
    avatar?: { url?: string | null } | null
  }>,
  availableTeams: Array<{ id: number; name: string }>,
  availableRoles: Array<{ id: number; name: string }>,
  treeRoleNameMap: Map<string, string>
): { label: string; avatarText: string; avatarSrc?: string } => {
  const strategyOption = getStrategyOptions().find((s) => s.value === strategy.type)
  const strategyLabel = strategyOption?.label || strategy.type

  if (strategy.type === 'manual_selection' && strategy.employeeId) {
    const employee = availableEmployees.find((emp) => emp.id === strategy.employeeId)
    const fullName = employee?.fullName || ''
    const initials = fullName
      .split(' ')
      .map((n) => n.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2)

    const avatarUrl = employee?.avatar?.url
    return {
      label: fullName,
      avatarSrc: avatarUrl ? buildUrl(avatarUrl) : undefined,
      avatarText: initials || 'U',
    }
  }

  if (strategy.teamId) {
    const team = availableTeams.find((t) => t.id === strategy.teamId)
    const teamName = team?.name || ''

    let label = ''
    if (strategy.type === 'team_leads') {
      label = `Team leads of ${teamName}`
    } else if (strategy.type === 'anyone_in_team') {
      label = `Anyone in ${teamName}`
    } else {
      label = `${strategyLabel} - ${teamName}`
    }

    return {
      label,
      avatarText: teamName.charAt(0).toUpperCase() || 'T',
      avatarSrc: undefined,
    }
  }

  if (strategy.roleId) {
    let roleName = ''

    if (strategy.roleId.startsWith('jobcatalog_treerole-')) {
      roleName = treeRoleNameMap.get(strategy.roleId) || ''
    } else {
      const role = availableRoles.find((r) => r.id === Number(strategy.roleId))
      roleName = role?.name || ''
    }

    return {
      label: `${strategyLabel} - ${roleName}`,
      avatarText: roleName.charAt(0).toUpperCase() || 'R',
      avatarSrc: undefined,
    }
  }

  return {
    label: strategyLabel,
    avatarText: strategyLabel.charAt(0).toUpperCase() || 'S',
    avatarSrc: undefined,
  }
}
